import dayjs from 'dayjs'
import {
  BOOKED_EVENT_MODES,
  currency_list,
  EVENT_TYPES_ARRAY,
  MOMENT_FORMATS,
  PAYMENT_METHOD_KV_MAP,
} from 'src/consumer/constants'
import { getAgeFromDobWithTime } from 'src/consumer/helpers/profilePageHelper'
import {
  getGenderFromGenderId,
  isValidNumber,
  maskString,
} from 'src/consumer/helpers/utilFunctions'
import {
  getPriceStringWithCurrency,
  isCommonResponseSuccessful,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import utc from 'dayjs/plugin/utc'
import { getData, postData } from 'src/consumer/services/profileService'
import { toast } from 'react-toastify'
import BorderAll from '@mui/icons-material/BorderAll'
import Email from '@mui/icons-material/Email'
import PictureAsPdf from '@mui/icons-material/PictureAsPdf'
import Send from '@mui/icons-material/Send'

dayjs.extend(utc)(utc)

export const EXPORT_TYPES = {
  excel: {
    id: 'excel',
    label: 'Excel',
    actionButtonText: 'Download',
    icon: BorderAll,
  },
  pdf: {
    id: 'pdf',
    label: 'PDF',
    actionButtonText: 'Download',
    icon: PictureAsPdf,
  },
  email_pdf: {
    id: 'email_pdf',
    label: 'Email PDF',
    actionButtonText: 'Send',
    icon: Email,
  },
  practitioner_pdf: {
    id: 'practitioner_pdf',
    label: 'Send to Practitioner',
    actionButtonText: 'Send Report',
    icon: Send,
  },
  support_pdf: {
    id: 'support_pdf',
    label: 'Send to Me',
    actionButtonText: 'Send Report',
    icon: Send,
  },
}

export function getSettlementDetails(payoutItem) {
  /* if collected by aumhum 
    return source share (aumhum ows {final digit } to clinic)
    else (meaning collected by them)
    fee paid - source share (Clinic ows {final digit} to aumhum)
    For belongs-to-clinic (not affiliated to AUMHUM): no AUMHUM settlement.
    */

  if (payoutItem?.affiliatedToAUMHUM === false) {
    return {
      color: 'warning',
      string: 'Excluded from payout calculations',
      payToAumhum: 0,
    }
  }

  let owingEntity = 'AUMHUM'
  let toOwningEntity = 'Clinic'

  let sourceShare = payoutItem?.sourceShare

  let finalDigit = 0

  let feeCollected = payoutItem?.feeCollected

  const isUsingMinPayableValue =
    isValidNumber(payoutItem?.minFeeUsedForCalculations) &&
    payoutItem?.minFeeUsedForCalculations > 0 &&
    payoutItem?.minFeeUsedForCalculations !== feeCollected

  if (isUsingMinPayableValue) {
    feeCollected = payoutItem?.minFeeUsedForCalculations
  }

  if (!isValidNumber(sourceShare)) {
    return { color: 'default', string: 'Cannot Calculate' }
  }

  if (
    payoutItem.paymentMethod ===
    PAYMENT_METHOD_KV_MAP.BOOKING_FEE_PAYMENT_METHOD_COLLECTED_BY_AUMHUM
  ) {
    finalDigit = sourceShare

    return {
      color: 'success',
      string: `Clinic to Collect ${getPriceStringWithCurrency(finalDigit, payoutItem?.currency)} from AUMHUM`,
      payToAumhum: -finalDigit,
    }
  } else {
    finalDigit = feeCollected - sourceShare

    return {
      color: 'error',
      string: `AUMHUM to Collect ${getPriceStringWithCurrency(finalDigit, payoutItem?.currency)} from Clinic`,
      payToAumhum: finalDigit,
    }
  }

  // return `${owingEntity} owes ${getPriceStringWithCurrency(
  //   finalDigit,
  //   payoutItem?.currency,
  // )} to ${toOwningEntity}`
}

export function areDatesInTheSameDay(currentDate, previousDate) {
  const dateForm = 'DD-MM-YYYY'
  const timeForm = 'HH:mm'

  if (currentDate.format(dateForm) === previousDate.format(dateForm)) {
    if (currentDate.format(timeForm) === '23:59' && previousDate.format(timeForm) === '00:00') {
      return true
    }
  }
  return false
}

export function getPayableToAUMHUMStringForClinicBrand(summaryReport, filters) {
  if (!filters?.clinicBrandId) {
    return 'Select Clinic brand or event type to continue'
  }
  return summaryReport?.overallPayableToAUMHUM > 0
    ? 'AUMHUM to Collect from Clinic'
    : 'Clinic to Collect from AUMHUM'
}
export function getPayableToAUMHUMStringForTherapist(summaryReport, filters) {
  if (!filters?.mentorId || !filters?.eventType) {
    return 'Select Therapist or event type to continue'
  }
  return summaryReport?.overallPayableToAUMHUM > 0
    ? 'AUMHUM to Collect from Clinic'
    : 'Clinic to Collect from AUMHUM'
}

//FOR BUILDING EXCEL SHEET
export function buildClinicPayoutReportsDoctor(item) {
  let formedItem = {}
  formedItem.patientNumber = item?.patientNumber
  formedItem.mentor = item?.mentorName
  formedItem.bookingTime = item?.collectedAt
    ? dayjs
        .utc(item?.collectedAt)
        .local()
        .format(MOMENT_FORMATS.completeTimeDetailsWithMonthShortForm)
    : 'No Data'
  formedItem.patientName = maskString(item?.patientName)
  const currencyItem = currency_list?.find((curr) => {
    return curr?.code === item?.currency
  })
  formedItem.transactionCurrency = currencyItem?.symbol || '₹'
  formedItem.patientGender = getGenderFromGenderId(item?.patientGender)?.label || 'NA'
  formedItem.patientAge = getAgeFromDobWithTime(item?.patientDateOfBirth)
  formedItem.visits = item?.sessionNumOfSameTypeWithSameMentor
  const foundEventType = EVENT_TYPES_ARRAY.find((type) => item?.eventType === type?.id)
  formedItem.mentorNoun = foundEventType.mentorNoun || 'Service Provider'
  formedItem.eventTypeTitle = foundEventType?.title
  formedItem.mode = BOOKED_EVENT_MODES?.[item?.mode]?.altTitle
  formedItem.affiliationStatus =
    item?.affiliatedToAUMHUM === false
      ? 'Clinic-associated practitioner (excluded from calculations)'
      : 'AUMHUM network'

  formedItem.feeCollected = prepareFeeCollectedStringForPayoutReports(item)
  formedItem.clinicSharePercentage = prepareClinicSharePercentageString(item)
  formedItem.clinicShare = prepareClinicShareStringForPayoutReports(item)
  formedItem.aumhumSharePercentage = prepareAumhumSharePercentageString(item)
  formedItem.aumhumShare = prepareAumhumShareStringForPayoutReports(item)
  formedItem.minimumFee = prepMinFeeString(item)
  formedItem.transactionDetails = getSettlementDetails(item).payToAumhum
  return formedItem
}

export function buildTherapistPayoutReportsExcel(item) {
  let formedItem = {}
  formedItem.patientNumber = item?.patientNumber
  formedItem.patientName = maskString(item?.patientName)
  formedItem.patientGender = getGenderFromGenderId(item?.patientGender)?.label || 'NA'
  formedItem.patientAge = getAgeFromDobWithTime(item?.patientDateOfBirth)

  const currencyItem = currency_list?.find((curr) => {
    return curr?.code === item?.currency
  })
  formedItem.transactionCurrency = currencyItem?.symbol || '₹'

  formedItem.visits = item?.sessionNumOfSameTypeWithSameMentor

  // Clinic details
  formedItem.clinic = item?.clinicBrandId
    ? `${item?.sourceName} ${item?.clinicLocationName || ''}`.trim()
    : 'Non Clinical Event'

  formedItem.bookingTime = item?.collectedAt
    ? dayjs
        .utc(item?.collectedAt)
        .local()
        .format(MOMENT_FORMATS.completeTimeDetailsWithMonthShortForm)
    : 'No Data'

  const foundEventType = EVENT_TYPES_ARRAY.find((type) => item?.eventType === type?.id)
  formedItem.eventTypeTitle = foundEventType?.title || ''
  formedItem.mode = BOOKED_EVENT_MODES?.[item?.mode]?.altTitle || ''

  // Session details
  formedItem.sessionCompleted = item?.markedCompleted ? 'Completed' : 'Not Completed'
  formedItem.nextVisitDateGiven = item?.nextVisitDate ? 'Yes' : 'No'
  formedItem.reportContentLength = isValidNumber(item?.reportContentLength)
    ? item?.reportContentLength
    : ''

  // Fee details
  formedItem.feeCollected = isValidNumber(item?.feeCollected) ? item?.feeCollected : ''

  // Mentor share details
  formedItem.mentorSharePercentage = isValidNumber(item?.mentorSharePercentage)
    ? `${item?.mentorSharePercentage}%`
    : ''
  formedItem.mentorShare = isValidNumber(item?.mentorShare) ? item?.mentorShare : ''

  // Min fee if applicable
  if (
    isValidNumber(item?.minFeeUsedForCalculations) &&
    item?.minFeeUsedForCalculations !== item?.feeCollected
  ) {
    formedItem.minimumFeeUsed = item?.minFeeUsedForCalculations
  } else {
    formedItem.minimumFeeUsed = ''
  }

  return formedItem
}

function prepareFeeCollectedStringForPayoutReports(report) {
  let finalString = ''
  const feePaid = report?.feeCollected

  finalString = feePaid

  // const isUsingMinPayableValue =
  //   isValidNumber(minPayableValue) && minPayableValue > 0 && minPayableValue !== feePaid
  // if (isUsingMinPayableValue) {
  //   finalString += `. Fee used for calculations is ${minPayableValue}`
  // }

  return finalString
}

function prepareClinicSharePercentageString(report) {
  const minPayableValue = report?.minFeeUsedForCalculations
  const currency = report?.currency
  const clinicSharePercentage = report?.sourceSharePercentage
  const feePaid = report?.feeCollected
  let finalString = ''
  const payingToAUMHUM = minPayableValue > feePaid
  if (payingToAUMHUM) {
    finalString += `${clinicSharePercentage}% of ${getPriceStringWithCurrency(
      minPayableValue,
      currency,
      true,
    )} of min payable amt`
  } else {
    if (isValidNumber(clinicSharePercentage)) {
      finalString += `${clinicSharePercentage}%`
    } else {
      return ''
    }
  }
  return finalString
}

function prepareClinicShareStringForPayoutReports(report) {
  let finalString = ''

  const currency = report?.currency
  const clinicShare = report?.sourceShare
  finalString += ''
  if (isValidNumber(clinicShare)) {
    finalString += clinicShare
  } else {
    return ''
  }

  return finalString
}

function prepareAumhumSharePercentageString(report) {
  const currency = report?.currency
  const minPayableValue = report?.minFeeUsedForCalculations
  const clinicSharePercentage = report?.sourceSharePercentage
  const isUsingMinPayableValue =
    isValidNumber(minPayableValue) && minPayableValue > 0 && minPayableValue
  return isValidNumber(clinicSharePercentage)
    ? `${100 - clinicSharePercentage}%${
        isUsingMinPayableValue
          ? ` of ${getPriceStringWithCurrency(minPayableValue, currency)} of min payable amt`
          : ''
      }`
    : ''
}
function prepareAumhumShareStringForPayoutReports(report) {
  let finalString = ''

  const feePaid = report?.feeCollected
  const currency = report?.currency
  const minPayableValue = report?.minFeeUsedForCalculations
  const clinicShare = report?.sourceShare
  const isUsingMinPayableValue =
    isValidNumber(minPayableValue) && minPayableValue > 0 && minPayableValue !== feePaid
  let feePaidForCalculations = report?.feeCollected
  if (isUsingMinPayableValue) {
    feePaidForCalculations = minPayableValue
  }

  finalString = `${
    isValidNumber((feePaidForCalculations || 0) - clinicShare)
      ? feePaidForCalculations - clinicShare
      : ''
  }`

  return finalString
}

function prepMinFeeString(report) {
  const feePaid = report?.feeCollected
  const currency = report?.currency
  const minPayableValue = report?.minFeeUsedForCalculations
  const clinicShare = report?.sourceShare
  const isUsingMinPayableValue =
    isValidNumber(minPayableValue) && minPayableValue > 0 && minPayableValue !== feePaid
  let feePaidForCalculations = report?.feeCollected

  return isUsingMinPayableValue ? minPayableValue : null
}

/** Default message when invoice/report generation exceeds server session limit (e.g. 500) */
export const INVOICE_SESSION_LIMIT_MESSAGE =
  'Too many sessions in this date range. Please narrow the date range or apply filters (maximum 500 sessions).'

/** Error codes the server may return when session limit is exceeded */
const SESSION_LIMIT_ERROR_CODES = [
  'SESSION_LIMIT_EXCEEDED',
  'MAX_SESSIONS_EXCEEDED',
  'INVOICE_SESSION_LIMIT_EXCEEDED',
]

function isSessionLimitErrorBody(body) {
  if (!body || typeof body !== 'object') return false
  const code = body?.code
  const message = body?.message || ''
  if (code && SESSION_LIMIT_ERROR_CODES.includes(String(code).toUpperCase())) return true
  if (/session.*limit|max.*session|500.*session/i.test(message)) return true
  return false
}

/**
 * Parse error response (from axios error or non-200 response) and show appropriate toast.
 * Handles blob responseType: reads blob as text and parses JSON when needed.
 * @param {*} error - axios error (error.response may have status and data)
 * @param {*} fallbackMessage - message when no structured error is found
 */
async function handleInvoiceApiError(error, fallbackMessage = 'Error in downloading reports') {
  let body = null
  const response = error?.response
  const data = response?.data

  if (data instanceof Blob) {
    try {
      const text = await data.text()
      body = text ? JSON.parse(text) : null
    } catch {
      body = null
    }
  } else if (data && typeof data === 'object') {
    body = data
  }

  if (isSessionLimitErrorBody(body)) {
    toast.error(body?.message || INVOICE_SESSION_LIMIT_MESSAGE)
  } else {
    toast.error(body?.message || fallbackMessage)
  }
}

function getPayoutInvoiceApiBase() {
  if (typeof window !== 'undefined') {
    const pathname = window.location.pathname
    if (pathname.startsWith('/support-home/') || pathname.startsWith('/support/')) {
      return '/api/secure/cc/support/invoices'
    }
  }
  return '/api/secure/l3/invoices'
}

export async function getPaypoutReportsPdfResponse(
  invoiceRequest,
  eventType,
  sendRazorpayNotifications = false,
  sendRazorpayReminderNotifications = false,
  addTdsRelatedInfo = false,
  blacklistedEventIds,
  includeAllSessionsInReport = false,
  hideGeneratedOn = false,
  includeNonAumhumAffiliations = false,
) {
  try {
    const resp = await postData(`${getPayoutInvoiceApiBase()}/download`, invoiceRequest, {
      responseType: 'blob', // Important: set responseType to blob
      params: {
        'event-type': eventType,
        'send-razorpay-notifications': sendRazorpayNotifications,
        'send-razorpay-reminder-notifications': sendRazorpayReminderNotifications,
        'show-tds-reduction': addTdsRelatedInfo,
        'blacklisted-events': blacklistedEventIds?.join(','),
        'include-all-events': includeAllSessionsInReport,
        'hide-generated-on': hideGeneratedOn,
        'include-non-aumhum-affiliations': includeNonAumhumAffiliations,
      },
    })

    if (resp?.status === 200 && resp.data) {
      // Get filename from Content-Disposition header
      const contentDisposition = resp.headers?.['content-disposition']
      let filename = 'invoice.pdf' // default filename
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '')
        }
      }

      // Create blob from response
      const blob = new Blob([resp.data], { type: 'application/pdf' })

      // Create download link
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename

      // Trigger download
      document.body.appendChild(link)
      link.click()

      // Cleanup
      window.URL.revokeObjectURL(url)
      document.body.removeChild(link)
      return resp
    } else {
      let body = null
      if (resp?.data instanceof Blob) {
        try {
          const text = await resp.data.text()
          body = text ? JSON.parse(text) : null
        } catch {
          body = null
        }
      }
      if (isSessionLimitErrorBody(body)) {
        toast.error(body?.message || INVOICE_SESSION_LIMIT_MESSAGE)
      } else {
        toast.error(body?.message || 'Error in downloading reports')
      }
      return null
    }
  } catch (error) {
    await handleInvoiceApiError(error, 'Error in downloading reports')
    return null
  }
}

export async function emailPayoutReportInvoicePdf(
  invoiceRequest,
  eventType,
  to,
  sendRazorpayNotifications = false,
  sendRazorpayReminderNotifications = false,
  addTdsRelatedInfo = false,
  blacklistedEventIds,
  includeAllSessionsInReport,
  hideGeneratedOn,
  includeNonAumhumAffiliations = false,
) {
  try {
    const resp = await postData(`${getPayoutInvoiceApiBase()}/email-pdf`, invoiceRequest, {
      params: {
        'event-type': eventType,
        'to-email': to,
        'send-razorpay-notifications': sendRazorpayNotifications,
        'send-razorpay-reminder-notifications': sendRazorpayReminderNotifications,
        'show-tds-reduction': addTdsRelatedInfo,
        'include-all-events': includeAllSessionsInReport,
        'hide-generated-on': hideGeneratedOn,
        'include-non-aumhum-affiliations': includeNonAumhumAffiliations,
      },
    })

    if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
      return resp
    } else {
      const body = resp?.data && typeof resp.data === 'object' ? resp.data : null
      if (isSessionLimitErrorBody(body)) {
        toast.error(body?.message || INVOICE_SESSION_LIMIT_MESSAGE)
      } else {
        toast.error(resp?.data?.message || 'Error in sending report by email.')
      }
      return null
    }
  } catch (error) {
    await handleInvoiceApiError(error, 'Error in sending report by email.')
    return null
  }
}

export async function sendPayoutReportInvoicePdfToPractitioner(
  invoiceRequest,
  eventType,
  deliveryModes = [],
  sendRazorpayNotifications = false,
  sendRazorpayReminderNotifications = false,
  addTdsRelatedInfo = false,
  blacklistedEventIds,
  includeAllSessionsInReport,
  hideGeneratedOn,
  includeNonAumhumAffiliations = false,
) {
  try {
    const resp = await postData(
      `${getPayoutInvoiceApiBase()}/send-to-practitioner`,
      {
        ...invoiceRequest,
        deliveryModes,
      },
      {
        params: {
          'event-type': eventType,
          'send-razorpay-notifications': sendRazorpayNotifications,
          'send-razorpay-reminder-notifications': sendRazorpayReminderNotifications,
          'show-tds-reduction': addTdsRelatedInfo,
          'blacklisted-events': blacklistedEventIds?.join(','),
          'include-all-events': includeAllSessionsInReport,
          'hide-generated-on': hideGeneratedOn,
          'include-non-aumhum-affiliations': includeNonAumhumAffiliations,
        },
      },
    )

    if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
      return resp
    }

    const body = resp?.data && typeof resp.data === 'object' ? resp.data : null
    if (isSessionLimitErrorBody(body)) {
      toast.error(body?.message || INVOICE_SESSION_LIMIT_MESSAGE)
    } else {
      toast.error(resp?.data?.message || 'Error in sending report to practitioner.')
    }
    return null
  } catch (error) {
    await handleInvoiceApiError(error, 'Error in sending report to practitioner.')
    return null
  }
}

export async function sendPayoutReportInvoicePdfToSupportUser(
  invoiceRequest,
  eventType,
  deliveryModes = ['mail', 'whatsapp'],
  sendRazorpayNotifications = false,
  sendRazorpayReminderNotifications = false,
  addTdsRelatedInfo = false,
  blacklistedEventIds,
  includeAllSessionsInReport,
  hideGeneratedOn,
  includeNonAumhumAffiliations = false,
) {
  try {
    const resp = await postData(
      `${getPayoutInvoiceApiBase()}/send-to-support-user`,
      {
        ...invoiceRequest,
        deliveryModes,
      },
      {
        params: {
          'event-type': eventType,
          'send-razorpay-notifications': sendRazorpayNotifications,
          'send-razorpay-reminder-notifications': sendRazorpayReminderNotifications,
          'show-tds-reduction': addTdsRelatedInfo,
          'blacklisted-events': blacklistedEventIds?.join(','),
          'include-all-events': includeAllSessionsInReport,
          'hide-generated-on': hideGeneratedOn,
          'include-non-aumhum-affiliations': includeNonAumhumAffiliations,
        },
      },
    )

    if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
      return resp
    }

    const body = resp?.data && typeof resp.data === 'object' ? resp.data : null
    if (isSessionLimitErrorBody(body)) {
      toast.error(body?.message || INVOICE_SESSION_LIMIT_MESSAGE)
    } else {
      toast.error(resp?.data?.message || 'Error in sending report to you.')
    }
    return null
  } catch (error) {
    await handleInvoiceApiError(error, 'Error in sending report to you.')
    return null
  }
}
